import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Card,
  EntitySelectorTrigger,
  Field,
  FieldLabel,
  Icon,
  VisualPicker,
  Input,
  Menu,
  MenuCheckboxItem,
  MenuGroup,
  MenuGroupLabel,
  MenuItem,
  MenuPopup,
  MenuSeparator,
  MenuTrigger,
  ScrollArea,
  Skeleton,
  Switch,
  Text,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
  useWidgetRegistry,
  type GlyphName,
  type IconName,
} from "@salestrak/ui";
import { arrayMove } from "@dnd-kit/sortable";
import {
  useIssueType,
  useIssueTypeMutations,
  type IssueTypeFieldItem,
} from "#app/hooks/settings/use-issue-type-mutations";
import { DangerZoneGroup } from "#app/components/settings/danger-zone";
import type { CustomFieldType } from "#app/lib/data";
import { FieldTypeGlyph } from "#app/components/settings/field-type-glyph";
import {
  SettingsGroup,
  SettingsGroupHeader,
  SettingsGroupRow,
  SettingsSection,
} from "./settings-group";
import { PROPERTY_ICON } from "#app/lib/constants/property-icons";
import { useAppNavigate } from "#app/lib/hooks/use-app-navigate";
import { useIssueSourceSettings } from "#app/lib/hooks/use-issue-source-settings";
import {
  ISSUE_PROPERTIES_WIDGET,
  ISSUE_TYPE_LAYOUT_SECTIONS,
  resolvePropertyOrder,
  resolveTypeSectionLayout,
  type OrderablePropertyKey,
} from "#app/widgets/issue/issue-summary-stack";
import { LoadErrorState } from "#app/components/settings/load-error-state";
import { calculateSortOrder } from "@salestrak/ui";
import { PageHeader, PageHeaderSubtitle, PageHeaderTitle, SETTINGS_CONTENT_MAX_WIDTH, SettingsPageGutter, SettingsBackBar, SettingsBackBarClearance, } from "#app/components/page-header";
import { SettingsPageSkeleton } from "#app/components/settings/settings-page-skeleton";
import { IssueTypeDeleteDialog } from "./issue-type-delete-dialog";
import { SystemMarker } from "#app/components/entity";
import {
  CustomFieldSelector,
  CustomFieldSelectorContent,
} from "#app/components/entity-selectors";
import { getFieldTypeMeta } from "#app/lib/constants/field-type-icons";
import { usePermission } from "#app/lib/hooks/use-permission";
import { useCustomFieldsDialogActions } from "#app/lib/custom-fields-dialog-context";
import { DisabledReason } from "#app/components/menus/disabled-reason";
import { toastMutationError } from "#app/lib/utils/toast-errors";
import { useDebouncedCallback } from "#app/lib/hooks/use-debounced-callback";

/** The glyph a project type wears until someone picks one — and what the
 *  picker's Reset returns it to. Types are NON-THEMED: the picker offers no
 *  color section and every surface renders the glyph muted (entity-visuals
 *  ignores the stored color), so no color is ever written from here. */
// The same default the server writes for a type nobody styled (issue-types.service).
const DEFAULT_TYPE_GLYPH: GlyphName = "symbol-deployed-code";

interface IssueTypeEditViewProps {
  issueTypeId: string;
  onBack: () => void;
}

/** How long a rename or description edit waits before it saves. */
const SAVE_DEBOUNCE_MS = 500;

/** A row's name with its quiet suffix ("· Required", "· Always shown") on
 *  the same line: one Text, the suffix an inline span inside it. Two
 *  siblings would stack — the row lays a node label out in a column. */
function RowTitle({ name, suffix }: { name: string; suffix?: string | null }) {
  return (
    <Text leading="tight" size="sm" weight="medium">
      {name}
      {suffix ? (
        <Text as="span" size="sm" variant="muted" weight="normal">
          {` · ${suffix}`}
        </Text>
      ) : null}
    </Text>
  );
}

/** A section a type cannot turn off wears the same switch as one it can —
 *  on, disabled, with the reason on hover — so the list reads as one column
 *  of switches rather than switches beside words. */
function AlwaysShownSwitch({ label }: { label: string }) {
  return (
    <DisabledReason reason="Always shown">
      <Switch checked disabled aria-label={`Show ${label}`} />
    </DisabledReason>
  );
}

/** The movable property rows a type lays out. Type, Workflow and Stage
 *  always lead and are always filled. Every row here can be required by the
 *  type; Source alone can also be switched OFF for the whole workspace on
 *  the Sources page, and then its row stands down here too. */
const PROPERTY_ROWS: Record<OrderablePropertyKey, { label: string; icon: IconName }> = {
  assignees: { label: "Assignees", icon: PROPERTY_ICON.assignee },
  teams: { label: "Teams", icon: PROPERTY_ICON.team },
  address: { label: "Address", icon: PROPERTY_ICON.address },
  source: { label: "Source", icon: PROPERTY_ICON.issueSource },
};
const REQUIRABLE_PROPERTY_KEYS = Object.keys(PROPERTY_ROWS) as OrderablePropertyKey[];

/** One labeled input's stand-in: the FieldLabel line over the 30px field. */
function InputFieldSkeleton({ labelWidth }: { labelWidth: number }) {
  return (
    <Box direction="column" gap="sm">
      <Box direction="column" height={20} justify="center">
        <Skeleton delayed height={12} width={labelWidth} />
      </Box>
      <Skeleton delayed fillWidth height={30} />
    </Box>
  );
}

/** The Name & Icon / Description card's stand-in, on the settled card's own
 *  padding and 2:3 split, so the inputs land where the bars were. */
function IssueTypeAboutSkeleton() {
  return (
    <Card render={<Box padding="md" />}>
      <Box direction="row" gap="lg" wrap>
        <Box flex={2} minWidth={200}>
          <InputFieldSkeleton labelWidth={88} />
        </Box>
        <Box flex={3} minWidth={200}>
          <InputFieldSkeleton labelWidth={72} />
        </Box>
      </Box>
    </Card>
  );
}

export function IssueTypeEditView({
  issueTypeId,
  onBack,
}: IssueTypeEditViewProps) {
  // Without the manage key the page is the type's read-only reference — the
  // same anatomy with the identity inputs disabled and the field roster
  // static (no add, no drag, no row menus, no danger zone).
  const canManage = usePermission("issue_types.manage");
  // The builder is its own gate: an admin who manages types but not fields
  // gets the picker without the shortcut.
  const canCreateFields = usePermission("custom_fields.manage");
  const { openDialog: openCustomFieldsDialog } = useCustomFieldsDialogActions();
  const [addFieldOpen, setAddFieldOpen] = useState(false);
  const registry = useWidgetRegistry();
  const { push } = useAppNavigate();
  const { settings: issueSourceSettings } = useIssueSourceSettings();
  const { issueType, isLoading, error, refetch } = useIssueType(issueTypeId);
  const {
    updateIssueType,
    deleteIssueType,
    setDefaultIssueType,
    reorderIssueTypeField,
    optimisticReorderField,
  } = useIssueTypeMutations();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [icon, setIcon] = useState<string>(DEFAULT_TYPE_GLYPH);
  const [showDelete, setShowDelete] = useState(false);
  const [pendingFieldIds, setPendingFieldIds] = useState<string[]>([]);

  const pendingRef = useRef<{ id: string; input: Record<string, any> } | null>(
    null,
  );

  const savePending = useCallback(() => {
    const pending = pendingRef.current;
    pendingRef.current = null;
    if (pending) updateIssueType(pending.id, pending.input);
  }, [updateIssueType]);
  const { debounced: scheduleSave, flush: flushPending } = useDebouncedCallback(
    savePending,
    SAVE_DEBOUNCE_MS,
  );

  // Flush — not drop — on unmount, so a rename typed right before
  // navigating away still lands.
  useEffect(() => () => flushPending(), [flushPending]);

  // Seed the local form ONCE per loaded type — keyed by id, NOT the object.
  // cache-and-network + the debounced save give `issueType` a new identity
  // on every mutation response, and re-seeding then stomps the inputs with
  // the last-SAVED text mid-typing (typing "residential" left "rs").
  const seededIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!issueType || seededIdRef.current === issueType.issueTypeId) return;
    seededIdRef.current = issueType.issueTypeId;
    setName(issueType.name);
    setDescription(issueType.description ?? "");
    setIcon(issueType.icon?.icon ?? DEFAULT_TYPE_GLYPH);
  }, [issueType]);

  const debouncedUpdate = useCallback(
    (input: Record<string, any>) => {
      // Edits for a DIFFERENT type than the pending batch save immediately;
      // same-type edits merge so name + description ride one mutation.
      if (pendingRef.current && pendingRef.current.id !== issueTypeId) {
        flushPending();
      }
      pendingRef.current = {
        id: issueTypeId,
        input: { ...pendingRef.current?.input, ...input },
      };
      scheduleSave();
    },
    [issueTypeId, flushPending, scheduleSave],
  );

  function handleNameChange(value: string) {
    setName(value);
    debouncedUpdate({ name: value });
  }

  function handleDescriptionChange(value: string) {
    setDescription(value);
    debouncedUpdate({ description: value });
  }

  async function handleDelete() {
    if (!issueType) return;
    await deleteIssueType(issueType.id);
    onBack();
  }

  const handleAddFields = useCallback(
    async (fieldIds: string[]) => {
      if (!issueType) return;
      const existingFields = (issueType.fields ?? []).map((f) => ({
        customFieldId: f.customFieldId,
        required: f.required,
      }));
      const existingIds = new Set(existingFields.map((f) => f.customFieldId));
      const newFields = fieldIds
        .filter((id) => !existingIds.has(id))
        .map((id) => ({
          customFieldId: id,
          required: false,
        }));
      if (newFields.length === 0) return;
      await updateIssueType(issueTypeId, {
        fields: [...existingFields, ...newFields],
      });
    },
    [issueType, issueTypeId, updateIssueType],
  );

  const handleFieldRequiredChange = useCallback(
    async (customFieldId: string, required: boolean) => {
      if (!issueType) return;
      const fields = (issueType.fields ?? []).map((f) => ({
        customFieldId: f.customFieldId,
        required: f.customFieldId === customFieldId ? required : f.required,
      }));
      await updateIssueType(issueTypeId, { fields });
    },
    [issueType, issueTypeId, updateIssueType],
  );

  const handleRemoveField = useCallback(
    async (customFieldId: string) => {
      if (!issueType) return;
      const fields = (issueType.fields ?? [])
        .filter((f) => f.customFieldId !== customFieldId)
        .map((f) => ({
          customFieldId: f.customFieldId,
          required: f.required,
        }));
      await updateIssueType(issueTypeId, { fields });
    },
    [issueType, issueTypeId, updateIssueType],
  );

  // A type picked on the Add Field picker's "Create Field" step: the picker
  // has closed itself; open the builder on the create form for that kind,
  // and attach the field here the moment it exists — the reader stays on
  // this page the whole way. `handleAddFields` is a real dependency: the
  // callback the builder holds must see the loaded type, not the first
  // render's `undefined`.
  const handleCreateType = useCallback(
    (type: CustomFieldType) => {
      setPendingFieldIds([]);
      openCustomFieldsDialog({
        createField: true,
        type,
        onCreated: (field) => {
          void handleAddFields([field.customFieldId]);
        },
      });
    },
    [handleAddFields, openCustomFieldsDialog],
  );

  const handleFieldAlwaysShownChange = useCallback(
    async (customFieldId: string, alwaysShown: boolean) => {
      if (!issueType) return;
      const fields = (issueType.fields ?? []).map((f) => ({
        customFieldId: f.customFieldId,
        required: f.required,
        alwaysShown: f.customFieldId === customFieldId ? alwaysShown : f.alwaysShown,
      }));
      await updateIssueType(issueTypeId, { fields });
    },
    [issueType, issueTypeId, updateIssueType],
  );

  const requiredProperties = useMemo(
    () => new Set(issueType?.requiredProperties ?? []),
    [issueType?.requiredProperties],
  );
  const handlePropertyRequiredChange = useCallback(
    async (key: string, required: boolean) => {
      if (!issueType) return;
      const next = new Set(requiredProperties);
      if (required) next.add(key);
      else next.delete(key);
      await updateIssueType(
        issueTypeId,
        { requiredProperties: REQUIRABLE_PROPERTY_KEYS.filter((k) => next.has(k)) },
        { optimistic: true },
      );
    },
    [issueType, issueTypeId, requiredProperties, updateIssueType],
  );

  const propertyOrder = useMemo(
    () => resolvePropertyOrder(issueType?.propertyOrder),
    [issueType?.propertyOrder],
  );
  const handlePropertyReorder = useCallback(
    async (activeId: string, overId: string) => {
      const from = propertyOrder.indexOf(activeId as OrderablePropertyKey);
      const to = propertyOrder.indexOf(overId as OrderablePropertyKey);
      if (from < 0 || to < 0 || from === to) return;
      await updateIssueType(
        issueTypeId,
        { propertyOrder: arrayMove(propertyOrder, from, to) },
        { optimistic: true },
      );
    },
    [issueTypeId, propertyOrder, updateIssueType],
  );
  const renderPropertyOverlay = useCallback(
    (id: string) => {
      const row = PROPERTY_ROWS[id as OrderablePropertyKey];
      return row ? <SettingsGroupRow label={row.label} icon={row.icon} /> : null;
    },
    [],
  );

  // The sections a project of this type shows, in the type's order, over the
  // roster the summary renders — the same resolution the summary runs, so
  // what the admin drags here is what a member reads.
  const sectionLayout = useMemo(
    () => resolveTypeSectionLayout(issueType, ISSUE_TYPE_LAYOUT_SECTIONS),
    [issueType],
  );
  const sectionIds = useMemo(
    () => sectionLayout.orderedWidgets.map((widget) => widget.type),
    [sectionLayout],
  );
  // Every write sends the WHOLE order: a non-empty order is what marks the
  // type as having a layout of its own (see `resolveTypeSectionLayout`).
  const writeSectionLayout = useCallback(
    async (order: string[], hidden: Record<string, boolean>) => {
      await updateIssueType(
        issueTypeId,
        {
          sectionOrder: order,
          hiddenSections: order.filter((type) => hidden[type]),
        },
        { optimistic: true },
      );
    },
    [issueTypeId, updateIssueType],
  );
  const handleSectionReorder = useCallback(
    async (activeId: string, overId: string) => {
      const from = sectionIds.indexOf(activeId);
      const to = sectionIds.indexOf(overId);
      if (from < 0 || to < 0 || from === to) return;
      await writeSectionLayout(arrayMove(sectionIds, from, to), sectionLayout.hidden);
    },
    [sectionIds, sectionLayout.hidden, writeSectionLayout],
  );
  const handleSectionVisibility = useCallback(
    async (type: string, visible: boolean) => {
      await writeSectionLayout(sectionIds, { ...sectionLayout.hidden, [type]: !visible });
    },
    [sectionIds, sectionLayout.hidden, writeSectionLayout],
  );
  const sectionTitle = useCallback(
    (type: string) => registry.get(type)?.title ?? type,
    [registry],
  );
  const renderSectionOverlay = useCallback(
    (id: string) => (
      <SettingsGroupRow
        label={sectionTitle(id)}
        icon={registry.get(id)?.icon ?? "layout-dashboard"}
      />
    ),
    [registry, sectionTitle],
  );

  const sortedFields = useMemo<IssueTypeFieldItem[]>(() => {
    const fields = (issueType?.fields ?? []) as IssueTypeFieldItem[];
    return [...fields].sort(
      (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0),
    );
  }, [issueType?.fields]);

  const fieldIds = useMemo(
    () => sortedFields.map((f) => f.customFieldId),
    [sortedFields],
  );

  const handleReorder = useCallback(
    async (activeId: string, overId: string) => {
      if (!issueType) return;
      const dragged = sortedFields.find((f) => f.customFieldId === activeId);
      const target = sortedFields.find((f) => f.customFieldId === overId);
      if (!dragged || !target) return;

      const others = sortedFields.filter(
        (f) => f.customFieldId !== dragged.customFieldId,
      );
      const overIndex = others.findIndex(
        (f) => f.customFieldId === target.customFieldId,
      );
      if (overIndex < 0) return;

      const placeAfter = dragged.sortOrder < target.sortOrder;
      const insertIndex = placeAfter ? overIndex + 1 : overIndex;
      const upper = others[insertIndex - 1]?.sortOrder ?? null;
      const lower = others[insertIndex]?.sortOrder ?? null;
      const newSortOrder = calculateSortOrder(upper, lower);

      if (dragged.sortOrder === newSortOrder) return;

      optimisticReorderField(issueType.id, dragged.customFieldId, newSortOrder);

      try {
        await reorderIssueTypeField(
          issueType.id,
          dragged.customFieldId,
          newSortOrder,
        );
      } catch (err) {
        toastMutationError(err, "Failed to reorder field");
      }
    },
    [issueType, sortedFields, optimisticReorderField, reorderIssueTypeField],
  );

  const renderFieldOverlay = useCallback(
    (id: string) => {
      const field = sortedFields.find((f) => f.customFieldId === id);
      if (!field?.field) return null;
      const meta = getFieldTypeMeta(field.field.type);
      return (
        <SettingsGroupRow
          label={field.field.name}
          subtitle={field.field.description ?? undefined}
          leading={<FieldTypeGlyph color={meta.color} icon={meta.icon} />}
        />
      );
    },
    [sortedFields],
  );

  const excludedFieldIds = useMemo(
    () => sortedFields.map((f) => f.customFieldId),
    [sortedFields],
  );

  if (isLoading && !issueType) {
    return (
      <Box direction="column" fill="flex" position="relative">
        {/* Real chrome: back bar + header geometry hold through the resolve;
          * the type name and its "N Projects" reach are bars. The body
          * mirrors the settled run: the Name/Description card, then the
          * three labeled, subtitled sections — Layout (four section rows),
          * Properties (the locked line + four movable rows) and Custom
          * Fields (its count/Add header over three reserved rows), and the
          * Danger zone card a manager gets. "Set type as default" is not
          * drawn: whether it exists is the answer being loaded. */}
        <SettingsBackBar label="Project Types" onClick={onBack} />
        <Box fill="flex" overflow="auto">
          <SettingsBackBarClearance />
          <PageHeader
            centered
            pinnedBack
            subtitle={
              <PageHeaderSubtitle>
                <Skeleton delayed height={12} width={72} />
              </PageHeaderSubtitle>
            }
          >
            <PageHeaderTitle>
              <Skeleton delayed height={20} width={180} />
            </PageHeaderTitle>
          </PageHeader>
          <SettingsPageSkeleton
            leading={<IssueTypeAboutSkeleton />}
            groups={[
              { rows: 4, subtitled: true },
              { rows: 5, subtitled: true },
              { rows: 3, subtitled: true, header: true },
              ...(canManage ? [{ rows: 1 }] : []),
            ]}
          />
        </Box>
      </Box>
    );
  }

  if (error && !issueType) {
    return (
      <LoadErrorState
        noun="Project type"
        error={error}
        onRetry={() => {
          void refetch();
        }}
      />
    );
  }

  if (!issueType) return null;

  const fieldCount = sortedFields.length;

  function handleIconChange(newIcon: GlyphName) {
    setIcon(newIcon);
    updateIssueType(issueTypeId, { icon: { icon: newIcon } });
  }

  function handleVisualReset() {
    handleIconChange(DEFAULT_TYPE_GLYPH);
  }

  return (
    <Box direction="column" fill="flex" position="relative">
      <SettingsBackBar label="Project Types" onClick={onBack} />
      <ScrollArea>
        <SettingsBackBarClearance />
        {/* The subtitle is the reach of a change here: every live Project
            wearing the type. */}
        <PageHeader
          centered
          pinnedBack
          subtitle={`${issueType.issueCount} ${issueType.issueCount === 1 ? "Project" : "Projects"}`}
        >
          <PageHeaderTitle
            adornment={
              issueType.isDefault ? (
                <SystemMarker
                  kind="default"
                  side="bottom"
                  subject="issueType"
                />
              ) : undefined
            }
          >
            {issueType.name}
          </PageHeaderTitle>
        </PageHeader>
        <Box align="center" paddingY="md" paddingX="md">
          <Box gap="2xl" fill="width" maxWidth={SETTINGS_CONTENT_MAX_WIDTH}>
            <Box gap="md">
              {/* Name & Description Card */}
              <Card render={<Box padding="md" />}>
                <Box direction="row" gap="lg" wrap>
                  {/* Box's `flex` token union is 1/none/auto/initial — arbitrary
                      grow factors are not tokens, so the 2:3 name/description
                      split rides an authored flex class. */}
                  <Box flex={2} minWidth={200}>
                    <Field>
                      <FieldLabel>Name & Icon</FieldLabel>
                      <Box direction="row" gap="sm" align="center" fill="width">
                        <VisualPicker
                          icon={icon as GlyphName}
                          defaultIcon={DEFAULT_TYPE_GLYPH}
                          disabled={!canManage}
                          onIconChange={handleIconChange}
                          // Offered only while the glyph has left the
                          // default: a Reset that would change nothing is
                          // noise.
                          onReset={
                            icon === DEFAULT_TYPE_GLYPH
                              ? undefined
                              : handleVisualReset
                          }
                          size="sm"
                          // Beside an outlined Input the neutral muted well
                          // nearly vanishes on a dark card; the outline
                          // variant shares the field's hairline instead.
                          variant="outline"
                        />
                        <Box flex={1}>
                          <Input
                            disabled={!canManage}
                            value={name}
                            onChange={(e) => handleNameChange(e.target.value)}
                            placeholder="Type name"
                          />
                        </Box>
                      </Box>
                    </Field>
                  </Box>
                  <Box flex={3} minWidth={200}>
                    <Field>
                      <FieldLabel>Description</FieldLabel>
                      <Input
                        disabled={!canManage}
                        value={description}
                        onChange={(e) => handleDescriptionChange(e.target.value)}
                        placeholder="Optional description"
                      />
                    </Field>
                  </Box>
                </Box>
              </Card>

              {/* Already default: the header marker states it, so no row.
                  Promoting another type is the only way OUT of default —
                  a workspace-wide choice, so the row is the manager's. */}
              {issueType.isDefault || !canManage ? null : (
                <SettingsGroup>
                  <SettingsGroupRow
                    label="Set type as default"
                    subtitle="New Projects use this Type"
                  >
                    <Button
                      onClick={() => setDefaultIssueType(issueTypeId)}
                      size="sm"
                      variant="ghost"
                    >
                      Set as default
                    </Button>
                  </SettingsGroupRow>
                </SettingsGroup>
              )}
            </Box>

            {/* How a Project of this type shows (the Layout: its sections,
                Properties locked first) and what it holds (its property band,
                then its custom fields) — three sections, one concern each.
                Members fold sections for themselves; order, requirements and
                availability are decided here. */}
            <SettingsSection
              label="Layout"
              subtitle="The sections a Project shows, in order"
            >
              <SettingsGroup
                {...(canManage
                  ? {
                      sortable: {
                        items: sectionIds,
                        onReorder: handleSectionReorder,
                        renderOverlay: renderSectionOverlay,
                      },
                    }
                  : {})}
              >
                {/* Properties lead every summary and nothing moves above them:
                    a static row outside the sortable run, so a drag can land
                    first in the run but never ahead of this. */}
                <SettingsGroupRow
                  quiet
                  icon={registry.get(ISSUE_PROPERTIES_WIDGET.type)?.icon ?? "list"}
                  label={sectionTitle(ISSUE_PROPERTIES_WIDGET.type)}
                >
                  <AlwaysShownSwitch label={sectionTitle(ISSUE_PROPERTIES_WIDGET.type)} />
                </SettingsGroupRow>
                {sectionLayout.orderedWidgets.map((widget) => (
                  <SettingsGroupRow
                    key={widget.type}
                    {...(canManage ? { sortableId: widget.type } : {})}
                    icon={registry.get(widget.type)?.icon ?? "layout-dashboard"}
                    label={sectionTitle(widget.type)}
                  >
                    {widget.hideable ? (
                      // The trigger is a wrapper around the switch, the way
                      // `DisabledReason` does it: the Switch primitive
                      // renders its own root and does not carry a
                      // TooltipTrigger's merged hover handlers through.
                      <Tooltip>
                        <TooltipTrigger render={<Box direction="row" align="center" />}>
                          <Switch
                            aria-label={`Show ${sectionTitle(widget.type)}`}
                            checked={!sectionLayout.hidden[widget.type]}
                            disabled={!canManage}
                            onCheckedChange={(visible) =>
                              handleSectionVisibility(widget.type, visible)
                            }
                          />
                        </TooltipTrigger>
                        <TooltipPopup>
                          {sectionLayout.hidden[widget.type] ? "Show" : "Hide"}
                        </TooltipPopup>
                      </Tooltip>
                    ) : (
                      <AlwaysShownSwitch label={sectionTitle(widget.type)} />
                    )}
                  </SettingsGroupRow>
                ))}
              </SettingsGroup>

            </SettingsSection>

            <SettingsSection
              label="Properties"
              subtitle="The properties a Project shows, and which are required to create it"
            >
              <SettingsGroup
                {...(canManage
                  ? {
                      sortable: {
                        items: propertyOrder,
                        onReorder: handlePropertyReorder,
                        renderOverlay: renderPropertyOverlay,
                      },
                    }
                  : {})}
              >
                <SettingsGroupRow quiet label="Type, Workflow, Stage" icon={PROPERTY_ICON.issueType} />
                {propertyOrder.map((key) => {
                  const row = PROPERTY_ROWS[key];
                  // Sources switched OFF on the Sources page hide the row on
                  // every Project, so here it dims, says "Off", and its
                  // Required switch stands down until the pool is back on.
                  const sourceOff = key === "source" && issueSourceSettings.projectSourcePolicy === "off";
                  const required = !sourceOff && requiredProperties.has(key);
                  const suffix = sourceOff ? "Off" : required ? "Required" : null;
                  return (
                    <SettingsGroupRow
                      key={key}
                      {...(canManage ? { sortableId: key } : {})}
                      icon={row.icon}
                      label={<RowTitle name={row.label} suffix={suffix} />}
                      quiet={sourceOff}
                    >
                      {canManage ? (
                        <Menu>
                          <MenuTrigger
                            render={
                              <Button
                                variant="ghost"
                                size="icon-xs"
                                aria-label={`${row.label} options`}
                              >
                                <Icon name="ellipsis" size="md" />
                              </Button>
                            }
                          />
                          <MenuPopup align="end" width="action">
                            <MenuGroup>
                              <MenuGroupLabel>Property options</MenuGroupLabel>
                              <MenuCheckboxItem
                                checked={required}
                                closeOnClick={false}
                                disabled={sourceOff}
                                disabledReason="Sources are off for Projects"
                                onCheckedChange={(next) =>
                                  handlePropertyRequiredChange(key, next)
                                }
                                variant="switch"
                              >
                                <Text>Required</Text>
                              </MenuCheckboxItem>
                            </MenuGroup>
                            {key === "source" ? (
                              <>
                                <MenuSeparator />
                                <MenuGroup>
                                  <MenuItem onClick={() => push("/settings/issue-sources")}>
                                    <Icon name="settings-2" size="md" color="muted" />
                                    <Text>Manage in Sources</Text>
                                  </MenuItem>
                                </MenuGroup>
                              </>
                            ) : null}
                          </MenuPopup>
                        </Menu>
                      ) : null}
                    </SettingsGroupRow>
                  );
                })}
              </SettingsGroup>
            </SettingsSection>

            <SettingsSection
              label="Custom Fields"
              subtitle="The Custom Fields a Project shows, and which are required to create it"
            >
              <SettingsGroup
                {...(canManage
                  ? {
                      sortable: {
                        items: fieldIds,
                        onReorder: handleReorder,
                        renderOverlay: renderFieldOverlay,
                      },
                    }
                  : {})}
              >
                <SettingsGroupHeader>
                  <Text size="sm" weight="medium">
                    {fieldCount} {fieldCount === 1 ? "Field" : "Fields"}
                  </Text>
                  {canManage ? (
                    <CustomFieldSelector
                      value={pendingFieldIds}
                      onValueChange={setPendingFieldIds}
                      exclude={excludedFieldIds}
                      open={addFieldOpen}
                      onOpenChange={(next) => {
                        setAddFieldOpen(next);
                        if (!next && pendingFieldIds.length > 0) {
                          handleAddFields(pendingFieldIds);
                          setPendingFieldIds([]);
                        }
                      }}
                      onCreateType={canCreateFields ? handleCreateType : undefined}
                    >
                      <EntitySelectorTrigger
                        render={
                          <Button size="sm" variant="ghost">
                            Add Field
                          </Button>
                        }
                      />
                      <CustomFieldSelectorContent align="end" />
                    </CustomFieldSelector>
                  ) : null}
                </SettingsGroupHeader>

                {sortedFields.map((field) => {
                  const fieldData = field.field;
                  if (!fieldData) return null;
                  const meta = getFieldTypeMeta(fieldData.type);
                  return (
                    <SettingsGroupRow
                      key={field.customFieldId}
                      {...(canManage
                        ? { sortableId: field.customFieldId }
                        : {})}
                      label={
                        <RowTitle
                          name={fieldData.name}
                          suffix={field.required ? "Required" : field.alwaysShown ? "Always shown" : null}
                        />
                      }
                      subtitle={fieldData.description ?? undefined}
                      leading={
                        <FieldTypeGlyph color={meta.color} icon={meta.icon} />
                      }
                    >
                      {canManage ? (
                      <Menu>
                        <MenuTrigger
                          render={
                            <Button
                              variant="ghost"
                              size="icon-xs"
                              aria-label="Field options"
                            >
                              <Icon name="ellipsis" size="md" />
                            </Button>
                          }
                        />
                        <MenuPopup align="end" width="action">
                          <MenuGroup>
                            <MenuGroupLabel>Field options</MenuGroupLabel>
                          <MenuCheckboxItem
                            checked={field.required}
                            closeOnClick={false}
                            onCheckedChange={(required) =>
                              handleFieldRequiredChange(field.customFieldId, required)
                            }
                            variant="switch"
                          >
                            <Text>Required</Text>
                          </MenuCheckboxItem>
                          <MenuCheckboxItem
                            checked={field.alwaysShown}
                            closeOnClick={false}
                            onCheckedChange={(alwaysShown) =>
                              handleFieldAlwaysShownChange(field.customFieldId, alwaysShown)
                            }
                            variant="switch"
                          >
                            <Text>Always shown</Text>
                          </MenuCheckboxItem>
                          </MenuGroup>
                          <MenuSeparator />
                          <MenuItem
                            onClick={() =>
                              handleRemoveField(field.customFieldId)
                            }
                          >
                            <Icon
                              name="trash"
                              size="md"
                              color="muted"
                            />
                            <Text>Remove Field</Text>
                          </MenuItem>
                        </MenuPopup>
                      </Menu>
                      ) : null}
                    </SettingsGroupRow>
                  );
                })}
              </SettingsGroup>
            </SettingsSection>

            {canManage ? (
              <DangerZoneGroup
                dangerZone={[
                  {
                    id: "delete-type",
                    label: "Delete type",
                    subtitle: "Its projects move to the default type.",
                    buttonLabel: "Delete type...",
                    variant: "destructive",
                    disabled: issueType.isDefault,
                    disabledReason: "Set another type as default first",
                    onClick: () => setShowDelete(true),
                  },
                ]}
              />
            ) : null}

            <SettingsPageGutter />
          </Box>
        </Box>
      </ScrollArea>

      <IssueTypeDeleteDialog
        open={showDelete}
        onOpenChange={setShowDelete}
        issueTypeName={issueType.name}
        issueCount={issueType.issueCount}
        onConfirm={handleDelete}
      />
    </Box>
  );
}
